/**
 * ScrollPage — wrapper estándar de rutas con scroll vertical.
 *
 * Se diseñó para vivir directamente dentro del `<main>` del RootLayout
 * (que es `overflow-hidden`, sin padding propio). Cada ruta envuelve
 * su contenido en `<ScrollPage>` para obtener:
 *   - altura completa del área de trabajo (`h-full`)
 *   - scroll vertical interno (`overflow-y-auto`)
 *   - padding consistente del producto (`px-8 py-10` por defecto)
 *
 * Para rutas que requieren un layout interno con zonas fijas (ej.
 * ProjectShell con header arriba + body abajo), NO usar `ScrollPage`.
 * En su lugar, el componente raíz de la ruta hace su propio
 * `<div className="h-full flex flex-col">` y gestiona el scroll dentro
 * de la zona que corresponda.
 *
 * Variantes:
 *   - `centered`: usa grid place-items-center, útil para login screens
 *     y empty states full-page.
 *   - `padding="tight"`: para rutas que ya tienen su propio header con
 *     padding (ej. subrutas del ProjectShell).
 */

import type { ReactNode } from 'react'

interface ScrollPageProps {
  children: ReactNode
  /** Patrón de padding interno. `default` = `px-8 py-10` (rutas
   *  top-level autenticadas). `tight` = `px-8 py-8` (subrutas dentro
   *  de un shell que ya tiene su propio padding/header). `none` = sin
   *  padding (la ruta provee el suyo). */
  padding?: 'default' | 'tight' | 'none'
  /** Si true, centra el contenido vertical y horizontalmente con
   *  `grid place-items-center`. Útil para login y empty states. */
  centered?: boolean
  /** className extra para casos especiales (max-width, bg, etc.). */
  className?: string
}

const PADDING_CLASS: Record<NonNullable<ScrollPageProps['padding']>, string> = {
  default: 'px-8 py-10',
  tight: 'px-8 py-8',
  none: '',
}

export function ScrollPage({
  children,
  padding = 'default',
  centered = false,
  className = '',
}: ScrollPageProps) {
  const base = centered
    ? 'h-full overflow-y-auto grid place-items-center'
    : 'h-full overflow-y-auto'
  const pad = PADDING_CLASS[padding]
  const finalClass = [base, pad, className].filter(Boolean).join(' ')
  return <div className={finalClass}>{children}</div>
}
